import React, { useEffect, useState, useCallback } from "react";
import Select from "react-select";
import axios  from "axios";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiTrash2, FiUpload } from "react-icons/fi";

import useUserLanguage from "../../hooks/useUserLanguage";
import useTranslations from "../../hooks/useTranslations";

const API = `${process.env.REACT_APP_API_BASE_URL}/api`;
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
});

const DropzoneBox = ({ files = [], onDrop, onRemove, placeholder }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: "image/jpeg",
    maxFiles: 200,
    onDrop,
  });

  const containerStyle = {
    border: "2px dashed #007bff",
    borderRadius: 8,
    padding: 12,
    cursor: "pointer",
    textAlign: "center",
    transition: ".2s",
  };
  const activeStyle = {
    borderColor: "#28a745",
    backgroundColor: "#e6ffed",
  };

  return (
    <section
      {...getRootProps()}
      style={isDragActive ? { ...containerStyle, ...activeStyle } : containerStyle}
    >
      <input {...getInputProps({ name: "files[]" })} />

      {files.length === 0 ? (
        <>
          <FiUpload size={24} className="text-primary" />
          <p className="mt-1 mb-0" style={{ fontSize: "0.85rem" }}>
            {placeholder}
          </p>
        </>
      ) : (
        <ul
          className="list-group list-group-flush mt-2"
          style={{
            maxHeight: 180, 
            overflowY: "auto",
            fontSize: "0.75rem",    // Daha küçük satırlar için
          }}
          onClick={e => e.stopPropagation()}
        >
          {files.map((file, idx) => (
            <li
              key={idx}
              className="list-group-item d-flex justify-content-between align-items-center py-1 px-2"
            >
              <span className="text-truncate" style={{ maxWidth: "85%" }}>
                {file.name}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger p-1"
                style={{ lineHeight: 1 }}
                onClick={() => onRemove(idx)}
              >
               ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};


export default function FormReaderCreate() {
  const [formData, setFormData] = useState({
    optical_form : null,
    institution  : null,
    class_       : null,
    educationYear: null,
    files        : []
  });
  const [nameListFile, setNameListFile] = useState(null);

  const [opticalOpts,     setOpticalOpts]     = useState([]);
  const [institutionOpts, setInstitutionOpts] = useState([]);
  const [classOpts,       setClassOpts]       = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [eduYearOpts, setEduYearOpts] = useState([]);

  const lang             = useUserLanguage();
  const { translations } = useTranslations(lang);
  const t = (k, d = "") => translations[k] || d;

  const navigate = useNavigate();

  const update      = (k, v) => setFormData(p => ({ ...p, [k]: v }));
  const updateFiles = fn     => setFormData(p => ({ ...p, files: fn(p.files) }));

  useEffect(() => {
    (async () => {
      try {
        const [forms, insts, classes, eduYears] = await Promise.all([
          axios.get(`${API}/optical-forms/list`,  authHeaders()),
          axios.get(`${API}/institution/list`,    authHeaders()),
          axios.get(`${API}/class/list`,          authHeaders()),
          axios.get(`${API}/education-years/list`, authHeaders()),
        ]);
        setOpticalOpts   (forms.data  .map(f => ({ value: f.id, label: f.title })));
        setInstitutionOpts(insts.data .map(i => ({ value: i.id, label: i.title })));
        setClassOpts     (classes.data.map(c => ({ value: c.id, label: c.title || c.name })));
        setEduYearOpts   (eduYears.data.map(e => ({ value: e.id, label: e.title })));
      } finally { setLoading(false); }
    })();
  }, []);

  const onFilesDrop = useCallback(accepted => {
    if (!accepted.length) return;
    const items = accepted.map(f =>
      Object.assign(f, { preview: URL.createObjectURL(f) })
    );
    updateFiles(prev => [...prev, ...items]);
  }, []);

  const removeFile = idx =>
    updateFiles(prev => prev.filter((_, i) => i !== idx));


const handleSubmit = async e => {
  e.preventDefault();
  try {
    // 1) FormData hazırla
    const fd = new FormData();
    fd.append("optical_form_id",   formData.optical_form?.value);
    fd.append("institution_id",    formData.institution?.value);
    fd.append("class_id",          formData.class_?.value);
    fd.append("education_year_id", formData.educationYear?.value);

    if (nameListFile) {
      fd.append("nameList", nameListFile, nameListFile.name);
    }

    formData.files.forEach(f => {
      fd.append("files[]", f, f.name);
    });

    // 2) Gönder
    await axios.post(`${API}/form-reader`, fd, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
        "Content-Type": "multipart/form-data"
      }
    });

    toast.success("Yükleme başarılı!");

    // (İsteğe bağlı yönlendirme)
    setTimeout(() => {
      navigate("/form/reader/table");
    }, 1500);

  } catch (err) {
    console.error("❌ Yükleme hatası:", err);
    toast.error("Yükleme başarısız oldu.");
  }
};


  return (
    <div className="card p-4">
      <form onSubmit={handleSubmit}>

        <label className="form-label fw-bold">{t("HDR_FORM", "Optik Formu Seç")}</label>
        <Select
          className="mb-3"
          options={opticalOpts}
          value={formData.optical_form}
          onChange={v => update("optical_form", v)}
          placeholder={t("PLACE_FORM", "Form seçiniz")}
        />

        <label className="form-label fw-bold">{t("HDR_INST", "Kurum Seç")}</label>
        <Select
          className="mb-3"
          options={institutionOpts}
          value={formData.institution}
          onChange={v => update("institution", v)}
          placeholder={t("PLACE_INST", "Kurum seçiniz")}
        />

        <label className="form-label fw-bold">{t("HDR_CLASS", "Sınıf Seç")}</label>
        <Select
          className="mb-3"
          options={classOpts}
          value={formData.class_}
          onChange={v => update("class_", v)}
          placeholder={t("PLACE_CLASS", "Sınıf seçiniz")}
        />

      <label className="form-label fw-bold">{t("HDR_EDU_YEAR","Eğitim Yılı Seç")}</label>
      <Select
        className="mb-3"
        isClearable
        options={eduYearOpts}
        value={formData.educationYear}
        onChange={v => update("educationYear", v)}
        placeholder={t("PLACE_EDU_YEAR","Eğitim yılı seçiniz")}
        required
      />
        {/* İsim Listesi (XLS/XLSX) */}
  <label className="form-label fw-bold">Excel İsim Listesi Yükle</label>
  <input
    type="file"
    className="form-control mb-3"
    accept=".xls,.xlsx"
    onChange={e => setNameListFile(e.target.files[0] || null)}
  />
  {/* ─── Örnek Excel Formatı ─── */}
<div className="mb-4">
  <small className="text-muted">
  Örnek Excel İsim Listesi:{" "}
  <a
    href={`${process.env.PUBLIC_URL}/documents/referans_isim_listesi.xls`}
    download
  >
    Buradan indirebilirsiniz
  </a>
</small>
</div>

        <label className="form-label fw-bold">{t("HDR_FILES", "Optik Formları Yükle")}</label>
        <DropzoneBox
          files={formData.files}
          onDrop={onFilesDrop}
          onRemove={removeFile}
          placeholder={t("UPLOAD_JPG", "JPG, PNG Dosyalarını Ekle")}
        />

        <div className="d-flex justify-content-center mt-4">
          <button className="btn btn-primary" disabled={loading}>
            {loading ? t("LOADING", "Yükleniyor…") : t("SAVE", "Kaydet")}
          </button>
        </div>
      </form>
    </div>
  );
}
